import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export interface MessageTemplate {
  id: string;
  channel: "EMAIL" | "WHATSAPP";
  name: string;
  subject: string | null;
  body: string;
  shared: boolean;
  mine?: boolean;
  author?: string;
}
const FIELDS: { key: string; label: string }[] = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "company", label: "Company" },
  { key: "email", label: "Their email" },
  { key: "phone", label: "Their phone" },
  { key: "deal_name", label: "Deal name" },
  { key: "amount", label: "Deal amount" },
  { key: "owner", label: "Owner" },
  { key: "my_name", label: "My name" },
  { key: "today", label: "Today" },
];
const input = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-primary";

/** Write, change and remove message templates; click a field to put it into the message where the cursor is */
export function TemplatesDialog({ open, onClose, channel, onChanged }: { open: boolean; onClose: () => void; channel: "EMAIL" | "WHATSAPP"; onChanged?: () => void }) {
  const { success, error } = useToast();
  const { user } = useAuth();
  const role = String((user as any)?.UserRole?.RoleCode || "").toUpperCase();
  const canShare = role === "SUPER_ADMIN" || role === "SALES_HEAD";
  const [rows, setRows] = useState<MessageTemplate[]>([]);
  const [edit, setEdit] = useState<Partial<MessageTemplate> | null>(null);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = () =>
    api
      .get(`/api/MessageTemplate/List?channel=${channel}`)
      .then((r) => setRows(unwrapArray<MessageTemplate>(r)))
      .catch(() => undefined);
  useEffect(() => {
    if (open) {
      setEdit(null);
      void load();
    }
  }, [open, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const insert = (key: string) => {
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    const cur = edit?.body || "";
    if (!el) return setEdit({ ...edit, body: cur + token });
    const s = el.selectionStart ?? cur.length;
    const e = el.selectionEnd ?? cur.length;
    setEdit({ ...edit, body: cur.slice(0, s) + token + cur.slice(e) });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + token.length, s + token.length);
    });
  };

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api.post("/api/MessageTemplate/Save", { id: edit.id, channel, name: edit.name, subject: channel === "EMAIL" ? edit.subject : undefined, body: edit.body, shared: !!edit.shared });
      success("Template saved", edit.name || "");
      setEdit(null);
      await load();
      onChanged?.();
    } catch (e: any) {
      error("Couldn't save the template", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (t: MessageTemplate) => {
    try {
      await api.post("/api/MessageTemplate/Delete", { id: t.id });
      await load();
      onChanged?.();
    } catch (e: any) {
      error("Couldn't remove it", e?.message || "Try again");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{channel === "EMAIL" ? "Email" : "WhatsApp"} templates</DialogTitle>
          <DialogDescription>Reusable messages. Merge fields such as {"{{first_name}}"} are filled in for the person or deal you are writing to.</DialogDescription>
        </DialogHeader>

        {!edit && (
          <section className="space-y-2">
            {rows.length === 0 && <p className="text-sm text-muted-foreground">No templates yet.</p>}
            {rows.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {t.name}
                    {t.shared && <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">Shared{t.mine === false && t.author ? ` by ${t.author}` : ""}</span>}
                  </div>
                  <div className="line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{t.subject ? `${t.subject}: ` : ""}{t.body}</div>
                </div>
                {t.mine !== false && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEdit(t)}>
                      Edit
                    </Button>
                    <Button size="icon-sm" variant="ghost" aria-label={`Remove ${t.name}`} onClick={() => remove(t)}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            <Button size="sm" onClick={() => setEdit({ channel, name: "", subject: "", body: "", shared: false })}>
              New template
            </Button>
          </section>
        )}

        {edit && (
          <section className="space-y-3">
            <label className="block space-y-1 text-xs font-medium text-muted-foreground">
              Name
              <input className={input} maxLength={80} value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. First reply" />
            </label>
            {channel === "EMAIL" && (
              <label className="block space-y-1 text-xs font-medium text-muted-foreground">
                Subject
                <input className={input} maxLength={200} value={edit.subject || ""} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} placeholder="e.g. Hello {{first_name}}" />
              </label>
            )}
            <label className="block space-y-1 text-xs font-medium text-muted-foreground">
              Message
              <textarea ref={bodyRef} rows={7} maxLength={4000} value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className={cn(input, "h-auto py-2 font-normal text-foreground")} />
            </label>
            <div className="flex flex-wrap gap-1">
              {FIELDS.map((f) => (
                <button key={f.key} type="button" onClick={() => insert(f.key)} className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">
                  + {f.label}
                </button>
              ))}
            </div>
            {canShare && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="size-4 accent-primary" checked={!!edit.shared} onChange={(e) => setEdit({ ...edit, shared: e.target.checked })} />
                Share with everyone
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEdit(null)}>
                Cancel
              </Button>
              <Button disabled={busy || !edit.name?.trim() || !edit.body?.trim() || (channel === "EMAIL" && !edit.subject?.trim())} onClick={save}>
                {busy ? "Saving…" : "Save template"}
              </Button>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
